import classNames from "classnames";
import { ReactNode } from "react";

/**
 * AiTeamCard — reusable dark card with a gradient visual block, floating
 * badge pill, product title (Inter Medium / tight), and description.
 *
 * Extracted from AiTeamSection so the same visual pattern can be reused
 * for any other "product highlight" grid across the site. The gradient
 * background and the mockup node are passed in as props — the card
 * itself is agnostic of the product kind.
 */

export type AiTeamCardProps = {
  /** Gradient + background tailwind classes for the visual block. */
  gradientClassName: string;
  /** Mockup rendered inside the gradient block (orbit, chat bubble, list, etc.). */
  mockup: ReactNode;
  /** Small pill label floating at the top of the visual block. Pass an
   *  empty string to hide — useful when the mockup (e.g. an SVG) already
   *  has the badge baked into its artwork. */
  badge: string;
  /** Product title — rendered as Inter Medium, tracking-tight. */
  title: string;
  description: string;
  className?: string;
};

const AiTeamCard = ({
  gradientClassName,
  mockup,
  badge,
  title,
  description,
  className,
}: AiTeamCardProps) => {
  return (
    <article
      className={classNames(
        "group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0e0e14] p-5 transition-colors hover:border-white/20",
        className
      )}
    >
      {/* Gradient visual block */}
      <div
        className={classNames(
          "relative overflow-hidden rounded-2xl",
          gradientClassName
        )}
      >
        {/* Floating badge — skipped when the mockup already renders its
            own badge (e.g. SVG artwork with the pill baked in). */}
        {badge ? (
          <div className="absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-neutralBlack px-3 py-1.5 font-inter text-[12px] font-medium text-white shadow-lg">
            <i className="fa-solid fa-sparkles text-[11px] text-brandSecondary" />
            <span className="whitespace-nowrap">{badge}</span>
          </div>
        ) : null}
        {mockup}
      </div>

      {/* Text */}
      <div className="px-1 pt-5">
        <h3 className="font-inter text-[22px] font-medium tracking-tight text-white md:text-[24px]">
          {title}
        </h3>
        <p className="mt-2 font-inter text-[14px] leading-relaxed text-darkSlate03 md:text-[15px]">
          {description}
        </p>
      </div>
    </article>
  );
};

export default AiTeamCard;
